import { Download, PageviewOutlined } from "@mui/icons-material";
import FolderIcon from "@mui/icons-material/Folder";
import { Box, Theme, Typography } from "@mui/material";
import {
  type GridRowParams,
  type GridCellParams,
  type GridColDef,
  GridActionsCellItem,
  type GridColumnVisibilityModel,
} from "@mui/x-data-grid";
import { type TFunction } from "i18next";
import { FileIcon } from "react-file-icon";
import ReactGA from "react-ga4";
import { type NavigateFunction } from "react-router-dom";
import { timestampToPrintableFormat } from "src/lib/date.helper";
import { fileIconStyles, formatSize, getFilenameExtension, isPreviewAvailable } from "src/lib/file.helper";

import { downloadHandler } from "../../helper/files.helper";
import { isFileItem, isFolderItem, type DataItem } from "../../interfaces/DataItem";

const getStyles = (theme: Theme) => ({
  cellContainer: { display: "flex", alignItems: "center", justifyContent: "start", gap: 2, maxWidth: "100%", height: "100%" },
  iconContainer: { display: "flex", alignItems: "center", justifyContent: "center", height: "100%", width: 32 },
  innerFileIconContainer: { display: "flex", width: 22 },
  textContainer: { display: "flex", flex: 1, overflow: "hidden", flexDirection: "column" },
  hideOnDesktop: { display: { xs: "block", md: "none" } },
  icon: { color: theme.palette.primary.main },
  folderIcon: { color: theme.palette.text.primary },
});

const trackDownload = (fileId: string, fileName: string) => {
  ReactGA.event("file_download", {
    fileName: fileName,
    fileId: fileId,
  });
};

const trackPreview = (fileId: string, fileName: string) => {
  ReactGA.event("file_preview", {
    fileName: fileName,
    fileId: fileId,
  });
};

const trackFolderOpen = (folderId: string) => {
  ReactGA.event("folder_open", {
    folderId: folderId,
  });
};

export const getFilesDataGridColumns = (
  t: TFunction<"translation", undefined>,
  navigate: NavigateFunction,
  locationId: string | undefined,
  theme: Theme
): Array<GridColDef<DataItem>> => [
  {
    field: "name",
    headerName: t("Name"),
    flex: 8,
    minWidth: 200,
    renderCell: (params: GridCellParams<DataItem>) => (
      <Box sx={getStyles(theme).cellContainer}>
        <Box sx={getStyles(theme).iconContainer}>
          {isFolderItem(params.row) && <FolderIcon sx={getStyles(theme).folderIcon} />}
          {isFileItem(params.row) && (
            <Box sx={getStyles(theme).innerFileIconContainer}>
              <FileIcon {...fileIconStyles[getFilenameExtension(params.row.file.fileName) ?? ""]} color={theme.palette.primary.light} />
            </Box>
          )}
        </Box>
        <Box
          sx={getStyles(theme).textContainer}
          onClick={() => {
            if (isFolderItem(params.row)) {
              trackFolderOpen(params.row.id);
              navigate(`/files/${params.row.id}`);
            } else if (isPreviewAvailable(params.row.file.fileName)) {
              trackPreview(params.row.file.id, params.row.file.fileName);
              navigate(`/files/${locationId}/${params.row.file.id}/${params.row.file.fileName}`);
            } else {
              trackDownload(params.row.file.id, params.row.file.fileName);
              void downloadHandler(params.row.file.id, params.row.file.fileName);
            }
          }}
        >
          <Typography variant='body1' fontSize={theme.fontSizes.sm} noWrap>
            {isFolderItem(params.row) ? params.row.name : params.row.file.fileName}
          </Typography>
          {isFileItem(params.row) && (
            <Typography sx={getStyles(theme).hideOnDesktop} fontSize={theme.fontSizes.xs} variant='body2' noWrap>
              {timestampToPrintableFormat(params.row.file.created)} | {formatSize(params.row.file.size)}
            </Typography>
          )}
        </Box>
      </Box>
    ),
  },
  {
    field: "created",
    flex: 2,
    minWidth: 140,
    headerName: t("Created At"),
    valueGetter: (_, row) => (isFileItem(row) ? row.file.created : null),
    valueFormatter: (value) => (value ? timestampToPrintableFormat(value) : null),
  },
  {
    field: "size",
    flex: 2,
    minWidth: 120,
    headerName: t("Size"),
    valueGetter: (_, row) => (isFileItem(row) ? row.file.size : null),
    valueFormatter: (value) => (value ? formatSize(value) : null),
  },
  {
    field: "actions",
    type: "actions",
    flex: 1,
    minWidth: 100,
    align: "left",
    getActions: (params: GridRowParams<DataItem>) => {
      if (isFileItem(params.row)) {
        if (isPreviewAvailable(params.row.file.fileName)) {
          return [
            <GridActionsCellItem
              color='primary'
              icon={<Download sx={getStyles(theme).icon} />}
              onClick={() => {
                if (isFileItem(params.row)) {
                  trackDownload(params.row.file.id, params.row.file.fileName);
                  void downloadHandler(params.row.file.id, params.row.file.fileName);
                }
              }}
              label='Download'
            />,
            <GridActionsCellItem
              color='primary'
              icon={<PageviewOutlined sx={getStyles(theme).icon} />}
              onClick={() => {
                if (isFileItem(params.row)) {
                  trackPreview(params.row.file.id, params.row.file.fileName);
                  navigate(`/files/${locationId}/${params.row.file.id}/${params.row.file.fileName}`);
                }
              }}
              label='Preview'
            />,
          ];
        }
        return [
          <GridActionsCellItem
            color='primary'
            icon={<Download sx={getStyles(theme).icon} />}
            onClick={() => {
              if (isFileItem(params.row)) {
                trackDownload(params.row.file.id, params.row.file.fileName);
                void downloadHandler(params.row.file.id, params.row.file.fileName);
              }
            }}
            label='Download'
          />,
        ];
      }
      return [];
    },
  },
];

export const mobileColumnVisibilityModel: GridColumnVisibilityModel = { created: false, size: false };
